import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Tooltip } from "antd";

import expandIcon from "../../assets/icons/sidebarExpand.svg";
import SqlCodeMirror from "./SqlCodeMirror";
import useUndoableState from "../../utilities/underedo/underedo";

const MIN_WIDTH = 300;
const MAX_WIDTH = 500;
const MIN_HEIGHT = 115;
const MAX_HEIGHT = 500

const RightQueryBar = ({ query, initialPosition }: any) => {

    const initialHeight =  MIN_HEIGHT;

    const [position, setPosition] = useState(initialPosition);
    const [size, setSize] = useState({ width: MIN_WIDTH, height: initialHeight });
    const [isMinimized, setIsMinimized] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const dragOffset = useRef({ x: 0, y: 0 });
    const initialSize = useRef({ width: MIN_WIDTH, height: initialHeight });
    const resizeStartMousePos = useRef({ x: 0, y: 0 });

    const { state: doc, setState: setDoc } = useUndoableState({ text: "" });

    useEffect(() => {
        setDoc({ text: query?.query || "" });
    }, [query, setDoc]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: window.innerWidth - e.clientX - dragOffset.current.x,
                    y: e.clientY - dragOffset.current.y,
                });
            } else if (isResizing) {
                const deltaX = resizeStartMousePos.current.x - e.clientX;
                const newWidth = Math.min(
                    MAX_WIDTH,
                    Math.max(MIN_WIDTH, initialSize.current.width + deltaX)
                );

                const newHeight = Math.min(
                    MAX_HEIGHT,
                    Math.max(
                        MIN_HEIGHT,
                        e.clientY - position.y
                    )
                );
                setSize({ width: newWidth, height: newHeight });
            }
        };

        const stopInteraction = () => {
            setIsDragging(false);
            setIsResizing(false);
        };

        if (isDragging || isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", stopInteraction);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", stopInteraction);
        };
    }, [isDragging, isResizing, position]);

    const handleDragStart = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragOffset.current = {
            x: window.innerWidth - e.clientX - position.x,
            y: e.clientY - position.y,
        };
        e.preventDefault();
    };

    const handleResizeStart = (e: React.MouseEvent) => {
        setIsResizing(true);
        initialSize.current = { ...size };
        resizeStartMousePos.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
        e.stopPropagation();
    };

    const toggleMinimize = () => setIsMinimized((prev) => !prev);

    const getTableName = () => {
        const name = query?.tablename;
        return name?.length > 25 ? (
            <Tooltip title={name}>{name.slice(0, 25) + "..."}</Tooltip>
        ) : (
            name
        );
    };

    return (
        <div
            className="rightQueryBar"
            style={{
                right: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                position: "absolute"
            }}
        >
            <div
                onMouseDown={handleDragStart}
                className={`headerRightBar ${isMinimized ? "headerMinimized" : "headerExpanded"}`}
            >
                <div style={{ flex: 1 }}>{getTableName()}</div>
                <button onClick={toggleMinimize} className="minimizeButton">
                    <img src={expandIcon} alt="toggle" />
                </button>
            </div>

            {!isMinimized && (
                <SqlCodeMirror
                    size={size}
                    doc={doc}
                    setDoc={setDoc}
                    isResizeActive={false}
                    handleResizeStart={handleResizeStart}
                />
            )}
        </div>

    );
};

export default RightQueryBar;
